import { useCallback, useEffect, useRef } from "react";

import { useSelector } from "react-redux";
import { isEqual } from "lodash-es";

import { useObjectMemo } from "../../../../hooks";
import { ACCOUNT_TYPE_OPTIONS } from "../consts";
import {
  ACHDestinationForm,
  ACHPaymentRecipientForm,
  RecipientProfileForm,
  type SwiftDestinationFormType,
  WireDestinationForm,
  transformRecipientApiFieldsToFormFields,
  transformACHApiFieldsToFormFields,
  transformWireApiFieldsToFormFields,
  wireDestinationInitialValues,
  recipientProfileInitialValues,
  achDestinationInitialValues,
  useValidationSchemaNoPoBox,
  useSwiftDestinationContext,
  createDefaultValues as createSwiftDestinationDefaultValues,
} from "../forms";
import {
  selectAllRecipients,
  selectOneRecipient,
  selectSortedRecipients,
} from "../selectors";
import { isAchRecipient } from "../../helpers/recipient.helpers";
import {
  useCreateOrUpdateACHDestination,
  useCreateOrUpdateRecipientProfile,
  useCreateOrUpdateSwiftDestination,
  useCreateOrUpdateWireDestination,
} from "../requests";

import type { ACHDestinationFormType, RecipientProfileType } from "../forms";

export const useAccountTypeOptions = () => {
  return Object.values(ACCOUNT_TYPE_OPTIONS);
};

export const useProfilePurposeOptions = () => {
  return {
    Person: "person",
    Business: "business",
  };
};

export const useACHDestinationForm = (
  recipientId?: API.RecipientId,
  initialValues?: Partial<ACHDestinationFormType>,
) => {
  const form = ACHDestinationForm.useForm();
  const { submitForm, resetForm } = form;
  const { createOrUpdateACHDestination, errorMessage, loading } =
    useCreateOrUpdateACHDestination();
  const recipient = useRecipient(recipientId) as API.Recipient;
  const recipientRef = useRef(recipient);

  useEffect(() => {
    recipientRef.current = recipient;
  }, [recipient]);

  useEffect(() => {
    if (recipient?.ach_destination) {
      const values = transformACHApiFieldsToFormFields(
        recipient?.ach_destination,
      );
      resetForm({ values });
    } else {
      resetForm({
        values: {
          ...achDestinationInitialValues,
          accountNumber:
            initialValues?.accountNumber ??
            achDestinationInitialValues.accountNumber,
          routingNumber:
            initialValues?.routingNumber ??
            achDestinationInitialValues.routingNumber,
          accountType:
            initialValues?.accountType ??
            achDestinationInitialValues.accountType,
        },
      });
    }
  }, [recipient?.ach_destination, resetForm, initialValues]);

  const handleSubmit = useCallback(
    async (onSuccess?: () => void, onError?: (error: any) => void) => {
      // validate form
      const result = await submitForm();
      if (!result.success) {
        // validation will produce field errors, we can just reject
        return Promise.reject();
      }
      return createOrUpdateACHDestination(recipientRef?.current)
        .then(() => {
          if (onSuccess) onSuccess();
        })
        .catch((error: any) => {
          if (!onError) return Promise.reject(error);
          if (error?.response?.json) {
            return error.response.json().then((err: any) => {
              onError(err?.message || JSON.stringify(err));
            });
          }
          onError(JSON.stringify(error));
          // if we got passed an onError, consider it handled
          return Promise.resolve();
        });
    },
    [submitForm, createOrUpdateACHDestination, recipientRef],
  );

  return useObjectMemo({
    loading,
    errorMessage,
    recipient,
    handleSubmit,
    form,
  });
};

export const useWireDestinationForm = (
  recipientId?: API.RecipientId,
  disallowAddressPoBox = false,
) => {
  const form = WireDestinationForm.useForm();
  const noPoBoxSchema = useValidationSchemaNoPoBox();
  const { createWireDestination, updateWireDestination, loading } =
    useCreateOrUpdateWireDestination();
  const { submitForm, resetForm, setValidationSchema } = form;
  const recipient = useRecipient(recipientId) as API.Recipient;

  useEffect(() => {
    if (recipient?.fedwire_destination) {
      const values = transformWireApiFieldsToFormFields(
        recipient.fedwire_destination,
      );
      resetForm({ values });
    } else {
      resetForm({ values: wireDestinationInitialValues });
    }
  }, [recipient?.fedwire_destination, resetForm]);

  useEffect(() => {
    if (disallowAddressPoBox) {
      setValidationSchema(noPoBoxSchema);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disallowAddressPoBox]);

  const handleSubmit = useCallback(
    async (
      dispatch: (recipient: API.Recipient, overrideRunProps?: any) => void,
      onSuccess?: () => void,
      onError?: (error: any) => void,
    ) => {
      // validate form
      const result = await submitForm();
      if (result.success) {
        // This returns a promise type but does not act like it: you must wrap try instead of using .catch()
        // you must override the run props' onSuccess, you cannot use .then()
        try {
          await dispatch(recipient, { onSuccess, onError });
        } catch (error: any) {
          let serverError = error;
          try {
            serverError = JSON.parse(error.response.bodyString).message;
          } catch {
            // ignore json parse errors
          }
          if (onError) onError(serverError);
        }
      }
    },
    [submitForm, recipient],
  );

  const handleSubmitCreate = useCallback(
    (onSuccess?: () => void, onError?: (error: any) => void) => {
      handleSubmit(createWireDestination, onSuccess, onError);
    },
    [createWireDestination, handleSubmit],
  );

  const handleSubmitUpdate = useCallback(
    (onSuccess?: () => void, onError?: (error: any) => void) => {
      handleSubmit(updateWireDestination, onSuccess, onError);
    },
    [updateWireDestination, handleSubmit],
  );

  return useObjectMemo({
    handleSubmitCreate,
    handleSubmitUpdate,
    form,
    loading,
  });
};

export const useACHPaymentRecipientForm = () => {
  const form = ACHPaymentRecipientForm.useForm();
  const { submitForm } = form;

  return useObjectMemo({
    form,
    submitForm,
  });
};

export const useRecipient = (recipientId?: API.RecipientId) => {
  return useSelector(
    (state) => selectOneRecipient(state, recipientId),
    isEqual,
  );
};

export const useRecipientProfileForm = (
  recipientId?: API.RecipientId,
  initialValues?: Partial<RecipientProfileType>,
) => {
  const form = RecipientProfileForm.useForm();
  const { submitForm, resetForm } = form;
  const { createOrUpdateRecipientProfile, error, errorMessage, loading } =
    useCreateOrUpdateRecipientProfile();
  const recipient = useRecipient(recipientId) as API.Recipient;

  useEffect(() => {
    if (recipient) {
      const values = transformRecipientApiFieldsToFormFields(recipient);
      resetForm({ values });
    } else {
      resetForm({
        values: {
          ...recipientProfileInitialValues,
          name: initialValues?.name ?? recipientProfileInitialValues.name,
          type: initialValues?.type ?? recipientProfileInitialValues.type,
        },
      });
    }
  }, [recipientId, resetForm, recipient, initialValues]);

  const handleSubmit = useCallback(
    async (
      onSuccess?: (data: API.CreateRecipientProfile.Response) => void,
      onError?: (error: any) => void,
    ) => {
      // validate form
      const result = await submitForm();
      if (result.success) {
        // passing in `onSuccess` does some magic in createACHProfile's `send`
        // `send` will call `onSuccess`
        createOrUpdateRecipientProfile(recipientId, { onSuccess, onError });
      }
    },
    [submitForm, createOrUpdateRecipientProfile, recipientId],
  );

  return useObjectMemo({
    form,
    handleSubmit,
    error,
    errorMessage,
    loading,
  });
};

export const useRecipients = () => {
  return useSelector(selectSortedRecipients);
};

export const useAllRecipients = () => {
  return useSelector(selectAllRecipients);
};

export const useSelectedRecipient = () => {
  const {
    values: { selectedRecipientId },
  } = ACHPaymentRecipientForm.useForm();
  return useRecipient(selectedRecipientId);
};

export const useHandleRecipientPageSubmit = ({
  onSubmitSuccessful,
}: {
  onSubmitSuccessful: () => void;
}) => {
  const selectedRecipient = useSelectedRecipient();
  const { submitForm: submitPaymentRecipientForm } =
    useACHPaymentRecipientForm();
  const { handleSubmit: submitPaymentDetailForm } = useACHDestinationForm(
    selectedRecipient?.id,
  );

  const handleRecipientPageSubmit = useCallback(async () => {
    const { success: recipientSuccess } = await submitPaymentRecipientForm();

    if (!selectedRecipient) {
      return;
    }

    // If the recipient is an ACH recipient, and the recipient form
    // passes validation, navigate to the payment details screen
    if (isAchRecipient(selectedRecipient) && recipientSuccess) {
      onSubmitSuccessful();
      return;
    }

    // if recipient is wire recipient, validate payment details form and create ACH destination
    if (recipientSuccess) {
      submitPaymentDetailForm(onSubmitSuccessful);
    }
  }, [
    selectedRecipient,
    submitPaymentDetailForm,
    submitPaymentRecipientForm,
    onSubmitSuccessful,
  ]);

  return handleRecipientPageSubmit;
};

export const useSwiftWireDestinationForm = (recipientId?: API.RecipientId) => {
  const swiftDestinationForm = useSwiftDestinationContext();
  const { formState, reset: resetForm, getValues } = swiftDestinationForm;
  const { createSwiftDestination, formErrors, setFormErrors, loading } =
    useCreateOrUpdateSwiftDestination();
  const recipient = useRecipient(recipientId) as API.Recipient;
  const swiftDestination = recipient?.swift_destination;
  const recipientRef = useRef(recipient);

  useEffect(() => {
    recipientRef.current = recipient;
  }, [recipient]);

  useEffect(() => {
    if (swiftDestination) {
      resetForm({
        ...swiftDestination,
        correspondent_bank_bic: swiftDestination.correspondent_bank_bic ?? "",
        isCorrespondentBankEnabled: Boolean(
          swiftDestination.correspondent_bank_bic,
        ),
        // swiftDestination serverside does not return renameFields or additionalFieldsToShow
        // so we need to default them to avoid the form thinking they've been changed
        renameFields: getValues("renameFields") ?? {},
        additionalFieldsToShow: getValues("additionalFieldsToShow") ?? {},
      });
    } else {
      const defaultValues = createSwiftDestinationDefaultValues();
      resetForm(defaultValues);
    }
  }, [getValues, swiftDestination, resetForm]);

  const handleCreate = useCallback(
    (
      values: SwiftDestinationFormType,
      onSuccess?: () => void,
      onError?: (error: any) => void,
    ) =>
      createSwiftDestination(
        recipientRef.current,
        { onSuccess: () => onSuccess?.(), onError },
        values,
      ).catch(() => {
        // the request has already passed the error to onError
      }),
    [createSwiftDestination],
  );

  return useObjectMemo({
    ...swiftDestinationForm,
    handleCreate,
    formState,
    formErrors,
    setFormErrors,
    loading,
  });
};
